<template>
  <ItemBox id="item-n-4" :visible="visible" title="Innovation Capability Index 2" :headerItems="headerItems"
    toggleClass="item4-toggle minus" :closeAction="closeItem" :graphViewClick="graphViewClick"
    :graphToggle="graphToggle">
    <div v-if="graphToggle" class="featured col-span-12 flex flex-col  h-96 overflow-y-auto  gap-3 p-2 pb-0 ">
      <div class="col-span-12" v-for="(factor, index) in [
        {
          mean: questionnaireResults.env_FACTORS,
          capabilities: [
            {
              capability: '1',
              mean: questionnaireResults.slr_SENSING,
            },
            {
              capability: '2',
              mean: questionnaireResults.srl_RESPONDING,
            },
            {
              capability: '3',
              mean: questionnaireResults.srl_LEARNING,
            },
          ]
        }
      ]">
        <div class="grid grid-cols-12 gap-2 p-2">

          <div class="col-span-8 grid gap-2">
            <div v-for="(capability) in factor.capabilities" :key="capability.capability"
              class=" col-span-8 grid grid-cols-8 gap-2 pt-2 px-2">

              <div class="flex col-span-2 rounded-xl p-1 items-center justify-between" :class="getBgClass(index)">
                <div class="flex justify-center w-full">
                  <h4 class="text-start">{{ capability.capability }}</h4>
                </div>
              </div>

              <div class="flex col-span-1  rounded-xl py-1 px-4 items-center justify-between"
                :class="getBgClass(index)">
                <div class="flex justify-center w-full">
                  <h4>{{ capability.mean }}</h4>
                </div>
              </div>

              <div class="flex flex-col h-full bg-[#fbfaff] rounded-full col-span-5 gap-2">
                <div class="resultProgressBar rounded-full h-full barTransition "
                  :class="getColorClass(capability.mean, 'bg')" :style="calculateBorder(capability.mean)"></div>
              </div>
            </div>
          </div>

          <div
            class="flex col-span-4 row-span-3 h-full border-2 border-[#aedddb] rounded-xl p-1 items-center justify-between">
            <div class="flex justify-center items-center w-full gap-2">

              <AgilityRatingPill :score="factor.mean" :fill="true" />

            </div>
          </div>

        </div>



      </div>
    </div>


    <div v-else class="featured grid col-span-12 flex flex-col gap-2">
      <div class="f-graph grid grid-cols-12 gap-2">

        <GraphIci :circles="dynamicCircles" :hoveredIndex="hoveredIndex" :setHoveredIndex="setHoveredIndex"
          :categories="categories" />

        <div class="flex flex-col col-span-2 pt-16 pr-12 font-semibold gap-4">
          <div class="flex items-center">
            <div class="shrink-0 grow-0 flex h-5 w-5 rounded-full border-2 border-black">
              <p class="invisible">helolo</p>
            </div>
            <p class="uppercase text-lg leading-5 text-start ml-4">Position of capability if aligned</p>
          </div>


        </div>

        <div class="flex flex-col gap-2 col-span-4 py-4 justify-center">

          <div class="flex justify-between border-2 border-[#aedddb] rounded-xl p-2 pl-3 w-full h-fit">
            <h4 class="leading-6">Category</h4>
            <div class="flex border-2 border-[#aedddb] rounded-lg p-1 leading-4">Mean</div>
          </div>

          <div v-for="(category, index) in categories" :key="index"
            class="flex justify-between rounded-xl p-2 pl-3 w-full h-fit duration-150 ease-in-out"
            @mouseover="setHoveredIndex(index)" @mouseleave="setHoveredIndex(null)"
            :class="hoveredIndex === index ? 'bg-[#aedddb]' : 'bg-[#fbfaff]'">
            <h4 class="leading-6">{{ category.name }}</h4>
            <div class="flex border-2 rating rounded-lg p-1 leading-4"
              :class="[getColorClass(category.mean, 'border'), { [getColorClass(category.mean, 'bg')]: hoveredIndex === index }]">
              {{ category.mean.toFixed(2) }}
            </div>
          </div>
        </div>

      </div>
    </div>

  </ItemBox>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import ItemBox from '@/components/results/ItemBox.vue'
import GraphIci from '@/components/results/GraphIci.vue'
import { getColorClass } from '@/utils/agilityRatings';
import AgilityRatingPill from '../AgilityRatingPill.vue';
import { QuestionnaireResults } from '@/data/QuestionnaireResults';

export default defineComponent({
  components: {
    ItemBox, GraphIci, AgilityRatingPill
  },
  data() {
    return {
      hoveredIndex: null as number | null,

      circles: [
        { cx: 600, cy: 276.5, r: 87.64, fill: '#82CA74', rotation: 0, translateY: 0, strokeTranslateY: 10 }, // Top Circle
        { cx: 400, cy: 823.5, r: 85.6, fill: '#82CA74', rotation: 0, translateY: 0, strokeTranslateY: 10 }, // Bottom Left Circle
        { cx: 800, cy: 823.5, r: 55, fill: '#F4A14A', rotation: 0, translateY: 0, strokeTranslateY: 10 } // Bottom Right Circle
      ],
      headerItems: [
        { name: 'Capabilities', class: 'col-span-2' },
        { name: 'Mean', class: 'col-span-1' },
        { name: 'Scale', class: 'col-span-5', scale: true },
        { name: 'Environmental Pressure', class: 'col-span-4 ', color: 'border-2 border-[#aedddb] bg-white' },
      ]
    }
  },
  props: {
    graphViewClick: {
      type: Boolean,
      required: false
    },
    graphToggle: {
      type: Boolean,
      required: false
    },
    calculateColor: {
      type: Function,
      required: true
    },
    calculateBorder: {
      type: Function,
      required: true
    },
    questionnaireResults: {
      type: Object as () => QuestionnaireResults,
      required: true
    },
    visible: {
      type: Boolean,
      required: true
    },
    closeAction: {
      type: Function,
      required: true
    },
    getBgClass: {
      type: Function,
      required: true
    }
  },
  methods: {
    closeItem() {
      this.closeAction();
    },
    setHoveredIndex(index: number | null) {
      this.hoveredIndex = index;
    },
    getColorClass
  },
  computed: {
    categories(): Array<{ name: string; mean: number }> {
      return [
        {
          name: 'Learning',
          mean: this.questionnaireResults.srl_LEARNING
        },
        {
          name: 'Responding',
          mean: this.questionnaireResults.srl_RESPONDING
        },
        {
          name: 'Sensing',
          mean: this.questionnaireResults.slr_SENSING
        }
      ];
    },
    dynamicCircles(): Array<{
      cx: number;
      cy: number;
      r: number;
      fill: string;
      rotation: number;
      translateY: number;
      strokeTranslateY: number;
      score: number;
    }> {
      const maxOffset = 400; // Increased from 323.5 to give more space from center
      const centerX = 600;
      const centerY = 600;

      return this.categories.map((category: {
        name: string,
        mean: number
      }, index: number) => {
        const score = parseFloat(category.mean.toString() || '0');
        const normalizedScore = Math.max(0, Math.min(10, score));

        const distance = (normalizedScore / 10) * maxOffset;
        const rotation = (360 / this.categories.length) * index;

        const colorClass = getColorClass(score, 'indicator');
        let fillColor = '#D4D4D4';

        if (colorClass === 'green') fillColor = '#82CA74';
        else if (colorClass === 'yellow') fillColor = '#F8D25B';
        else if (colorClass === 'orange') fillColor = '#F4A14A';
        else if (colorClass === 'red') fillColor = '#F07620';

        const minRadius = 40;
        const maxRadius = 100;
        const radius = minRadius + ((maxRadius - minRadius) * (normalizedScore / 10));

        return {
          cx: centerX,
          cy: centerY,
          r: radius,
          fill: fillColor,
          rotation: rotation,
          translateY: -distance,
          strokeTranslateY: -distance * 0.7, // No longer used but kept for compatibility
          score: score
        };
      });
    }
  }
});
</script>
